import DownloadIcon from '@mui/icons-material/Download';
import { Alert, Button, CircularProgress } from '@mui/material';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';

import { authStore } from '../../auth/authStore';
import type { CanvassingSessionExportItem } from './canvassingSessionExportApi';
import { getCanvassingSessionExportDownload } from './canvassingSessionExportApi';
import type { CanvassingSessionFetchImpl } from './canvassingSessionApi';

const DOWNLOAD_ERROR_MESSAGE = 'Could not prepare the session export download. Try again.';

const activateDownload = (url: string, fileName: string): void => {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.rel = 'noopener';
  document.body.append(link);
  try {
    link.click();
  } finally {
    link.remove();
  }
};

export type CanvassingSessionExportDownloadButtonProps = {
  exportItem: CanvassingSessionExportItem;
  fetchImpl?: CanvassingSessionFetchImpl;
};

type DownloadState = {
  auth: ReturnType<typeof authStore.getAuth>;
  downloading: boolean;
  error: string | null;
};

const subscribeAuth = (onChange: () => void) => authStore.subscribe(() => onChange());

export const CanvassingSessionExportDownloadButton = ({
  exportItem,
  fetchImpl,
}: CanvassingSessionExportDownloadButtonProps) => {
  const auth = useSyncExternalStore(subscribeAuth, authStore.getAuth);
  const authRef = useRef(auth);
  authRef.current = auth;
  const requestRef = useRef<AbortController | null>(null);
  const generationRef = useRef(0);
  const [scopedState, setScopedState] = useState<DownloadState>({ auth, downloading: false, error: null });
  const state = scopedState.auth === auth ? scopedState : { auth, downloading: false, error: null };

  useEffect(() => {
    return () => {
      generationRef.current += 1;
      requestRef.current?.abort();
      requestRef.current = null;
    };
  }, [auth]);

  const handleDownload = async (): Promise<void> => {
    if (requestRef.current !== null || auth === null) return;

    const requestAuth = auth;
    const controller = new AbortController();
    const generation = ++generationRef.current;
    requestRef.current = controller;
    setScopedState({ auth: requestAuth, downloading: true, error: null });
    const isCurrent = (): boolean =>
      !controller.signal.aborted &&
      requestRef.current === controller &&
      generationRef.current === generation &&
      authRef.current === requestAuth;

    try {
      const result = await getCanvassingSessionExportDownload({
        id: exportItem.id,
        signal: controller.signal,
        fetchImpl,
      });
      if (!isCurrent()) return;
      activateDownload(result.url, result.fileName);
    } catch {
      if (!isCurrent()) return;
      requestRef.current = null;
      setScopedState({ auth: requestAuth, downloading: false, error: DOWNLOAD_ERROR_MESSAGE });
      return;
    }

    if (!isCurrent()) return;
    setScopedState({ auth: requestAuth, downloading: false, error: null });
    requestRef.current = null;
  };

  return (
    <>
      <Button
        variant="outlined"
        onClick={() => void handleDownload()}
        disabled={state.downloading}
        aria-busy={state.downloading}
        startIcon={state.downloading ? <CircularProgress size={16} aria-hidden /> : <DownloadIcon />}
      >
        Download
      </Button>
      {state.error !== null && <Alert severity="error" role="alert">{state.error}</Alert>}
    </>
  );
};
